import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import toast, { Toaster } from "react-hot-toast";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  AiOutlineHome,
  AiOutlineCheckSquare,
  AiOutlineUser,
  AiOutlineFileText,
  AiOutlineDashboard,
  AiOutlinePlus,
  AiOutlineSearch,
  AiOutlineFilter,
  AiOutlineEye,
  AiOutlineCheck,
  AiOutlineClose,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlineReload,
  AiOutlineExport,
} from "react-icons/ai";
import { FiBell, FiUsers, FiTrendingUp } from "react-icons/fi";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import axios from "axios";

// Lottie Animations (helpers/animations/ klasöründen)
import loadingAnim from "../../helpers/animations/loading-pulse.json";
import successAnim from "../../helpers/animations/task-complete.json";
import chartAnim from "../../helpers/animations/data-chart.json";
import emptyAnim from "../../helpers/animations/empty-state.json";
import reviewAnim from "../../helpers/animations/writing.json";
import "../../assets/css/AdminTaskManagementPage.css";

const AdminTaskManagementPage = () => {
  const [activeTab, setActiveTab] = useState("listings");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [meetingNotes, setMeetingNotes] = useState([]);

  // Tab verileri
  const tabs = [
    { id: "listings", label: "Listing Review", icon: "📝", color: "#EA580C" },
    { id: "tasks", label: "Task Management", icon: "📊", color: "#DC2626" },
    { id: "users", label: "User Management", icon: "👥", color: "#16A34A" },
    { id: "meetings", label: "Meeting Notes", icon: "📋", color: "#7C3AED" },
  ];

  // Turuncu renk paleti
  const colors = {
    primary: "#EA580C",
    primaryLight: "#FDBA74",
    primaryDark: "#C2410C",
    background: "#FFF7ED",
    card: "#FFFFFF",
    textDark: "#7C2D12",
    textLight: "#92400E",
    success: "#16A34A",
    warning: "#D97706",
    error: "#DC2626",
  };

  // Data fetching
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchStats(),
        fetchPendingListings(),
        fetchAllTasks(),
        fetchAllUsers(),
        fetchMeetingNotes(),
      ]);
    } catch (error) {
      toast.error("Error loading dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${settings.apiURL}/get_dashboard_stats.php`,
        {
          headers: authHeader(),
        }
      );
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchPendingListings = async () => {
    try {
      const response = await axios.get(
        `${settings.apiURL}/get_pending_listings.php`,
        {
          headers: authHeader(),
        }
      );
      if (response.data.success) {
        setListings(response.data.listings);
      }
    } catch (error) {
      console.error("Error fetching listings:", error);
    }
  };

  const fetchAllTasks = async () => {
    try {
      const response = await axios.get(`${settings.apiURL}/get_all_tasks.php`, {
        headers: authHeader(),
      });
      if (response.data.success) {
        setTasks(response.data.tasks);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(
        `${settings.apiURL}/get_all_users_admin.php`,
        {
          headers: authHeader(),
        }
      );
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchMeetingNotes = async () => {
    try {
      const response = await axios.get(
        `${settings.apiURL}/get_meeting_notes.php`,
        {
          headers: authHeader(),
        }
      );
      if (response.data.success) {
        setMeetingNotes(response.data.notes);
      }
    } catch (error) {
      console.error("Error fetching meeting notes:", error);
    }
  };

  // Listing actions
  const handleListingAction = async (listingId, status, adminNotes = "") => {
    try {
      const response = await axios.post(
        `${settings.apiURL}/update_listing_status.php`,
        {
          listing_id: listingId,
          status: status,
          admin_notes: adminNotes,
        },
        { headers: authHeader() }
      );

      if (response.data.success) {
        toast.success(`Listing ${status} successfully!`);
        fetchPendingListings();
        fetchStats();
      }
    } catch (error) {
      toast.error("Failed to update listing");
    }
  };

  // Task actions
  const handleUpdateTask = async (taskId, taskData) => {
    try {
      const response = await axios.post(
        `${settings.apiURL}/update_task.php`,
        {
          task_id: taskId,
          ...taskData,
        },
        { headers: authHeader() }
      );

      if (response.data.success) {
        toast.success("Task updated successfully!");
        fetchAllTasks();
        fetchStats();
        return true;
      } else {
        toast.error(response.data.message || "Failed to update task");
        return false;
      }
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
      return false;
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const response = await axios.post(
        `${settings.apiURL}/delete_task.php`,
        {
          task_id: taskId,
        },
        { headers: authHeader() }
      );

      if (response.data.success) {
        toast.success("Task deleted successfully!");
        fetchAllTasks();
        fetchStats();
        return true;
      } else {
        toast.error(response.data.message || "Failed to delete task");
        return false;
      }
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
      return false;
    }
  };

  if (loading) {
    return (
      <div className="admin-loading-container">
        <div className="admin-loading-content">
          <Lottie
            animationData={loadingAnim}
            loop={true}
            style={{ width: 120, height: 120 }}
          />
          <p className="admin-loading-text">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: colors.card,
            color: colors.textDark,
            border: `2px solid ${colors.primaryLight}`,
            borderRadius: "12px",
            fontSize: "14px",
            fontWeight: "500",
          },
          success: {
            iconTheme: {
              primary: colors.success,
              secondary: colors.card,
            },
          },
          error: {
            iconTheme: {
              primary: colors.error,
              secondary: colors.card,
            },
          },
        }}
      />

      {/* Header */}
      <AdminHeader colors={colors} onRefresh={fetchDashboardData} />

      <div className="admin-layout">
        {/* Sidebar */}
        <AdminSidebar
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
        />

        {/* Main Content */}
        <div className="admin-main-content">
          {/* Stats Cards */}
          <StatsSection stats={stats} colors={colors} />

          {/* Tab Content */}
          <div className="admin-tab-content">
            <AnimatePresence mode="wait">
              {activeTab === "listings" && (
                <ListingReviewTab
                  listings={listings}
                  onListingAction={handleListingAction}
                  colors={colors}
                />
              )}
              {activeTab === "tasks" && (
                <TaskManagementTab
                  tasks={tasks}
                  colors={colors}
                  onRefreshTasks={fetchAllTasks}
                  onRefreshStats={fetchStats}
                  onUpdateTask={handleUpdateTask}
                  onDeleteTask={handleDeleteTask}
                  users={users}
                />
              )}
              {activeTab === "users" && (
                <UserManagementTab
                  users={users}
                  colors={colors}
                  onRefreshUsers={fetchAllUsers}
                />
              )}
              {activeTab === "meetings" && (
                <MeetingNotesTab
                  meetingNotes={meetingNotes}
                  colors={colors}
                  onRefreshNotes={fetchMeetingNotes}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminHeader = ({ colors, onRefresh }) => (
  <motion.header
    className="admin-header"
    initial={{ y: -50, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    style={{
      background: colors.card,
      borderBottom: `2px solid ${colors.primaryLight}`,
    }}
  >
    <div className="admin-header-content">
      <div className="admin-header-left">
        <button
          className="home-button"
          onClick={() => (window.location.href = "/home")}
          style={{
            background: colors.primary,
            color: "white",
            border: `2px solid ${colors.primaryDark}`,
          }}
        >
          <AiOutlineHome style={{ marginRight: "8px" }} />
          Home
        </button>
        <h1 className="admin-title" style={{ color: colors.textDark }}>
          Admin Dashboard
        </h1>
      </div>

      <div className="admin-header-right">
        <button
          className="refresh-button"
          onClick={onRefresh}
          style={{
            background: colors.background,
            color: colors.textDark,
            border: `2px solid ${colors.primaryLight}`,
          }}
        >
          <AiOutlineReload style={{ marginRight: "6px" }} />
          Refresh
        </button>
      </div>
    </div>
  </motion.header>
);

const AdminSidebar = ({ tabs, activeTab, setActiveTab, colors }) => (
  <motion.div
    className="admin-sidebar"
    initial={{ x: -100, opacity: 0 }}
    animate={{ x: 0, opacity: 1 }}
    style={{
      background: colors.card,
      borderRight: `2px solid ${colors.primaryLight}`,
    }}
  >
    <div
      className="sidebar-header"
      style={{ borderBottom: `1px solid ${colors.primaryLight}` }}
    >
      <FiTrendingUp size={24} style={{ color: colors.primary }} />
      <span style={{ color: colors.textDark, fontWeight: "600" }}>
        Admin Center
      </span>
    </div>

    <nav className="sidebar-nav">
      {tabs.map((tab) => (
        <motion.button
          key={tab.id}
          className={`sidebar-tab ${activeTab === tab.id ? "active" : ""}`}
          onClick={() => setActiveTab(tab.id)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          style={{
            background: activeTab === tab.id ? colors.primary : "transparent",
            color: activeTab === tab.id ? "white" : colors.textDark,
            border: `2px solid ${
              activeTab === tab.id ? colors.primaryDark : colors.primaryLight
            }`,
          }}
        >
          <span className="tab-icon" style={{ fontSize: "1.2em" }}>
            {tab.icon}
          </span>
          <span className="tab-label">{tab.label}</span>
        </motion.button>
      ))}
    </nav>
  </motion.div>
);

const StatsSection = ({ stats, colors }) => (
  <motion.div
    className="stats-section"
    initial={{ y: 20, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ delay: 0.2 }}
  >
    <div className="stats-grid">
      {/* Pending Listings Stat */}
      <motion.div
        className="stat-card"
        whileHover={{ scale: 1.05, y: -5 }}
        style={{
          background: `linear(135deg, ${colors.primaryLight}20, ${colors.primary}20)`,
          border: `2px solid ${colors.primaryLight}`,
        }}
      >
        <div className="stat-icon" style={{ background: colors.primary }}>
          📝
        </div>
        <div className="stat-content">
          <div className="stat-value" style={{ color: colors.textDark }}>
            {stats?.listings?.pending_listings || 0}
          </div>
          <div className="stat-label" style={{ color: colors.textLight }}>
            Pending Listings
          </div>
        </div>
      </motion.div>

      {/* Urgent Tasks Stat */}
      <motion.div
        className="stat-card"
        whileHover={{ scale: 1.05, y: -5 }}
        style={{
          background: `linear(135deg, #FECACA20, ${colors.error}20)`,
          border: `2px solid #FECACA`,
        }}
      >
        <div className="stat-icon" style={{ background: colors.error }}>
          📊
        </div>
        <div className="stat-content">
          <div className="stat-value" style={{ color: colors.textDark }}>
            {stats?.tasks?.urgent_tasks || 0}
          </div>
          <div className="stat-label" style={{ color: colors.textLight }}>
            Urgent Tasks
          </div>
        </div>
      </motion.div>

      {/* Active Users Stat */}
      <motion.div
        className="stat-card"
        whileHover={{ scale: 1.05, y: -5 }}
        style={{
          background: `linear(135deg, #BBF7D020, ${colors.success}20)`,
          border: `2px solid #BBF7D0`,
        }}
      >
        <div className="stat-icon" style={{ background: colors.success }}>
          👥
        </div>
        <div className="stat-content">
          <div className="stat-value" style={{ color: colors.textDark }}>
            {stats?.users?.active_users_week || 0}
          </div>
          <div className="stat-label" style={{ color: colors.textLight }}>
            Active Users
          </div>
        </div>
      </motion.div>

      {/* Approved Today Stat */}
      <motion.div
        className="stat-card"
        whileHover={{ scale: 1.05, y: -5 }}
        style={{
          background: `linear(135deg, #FEF3C720, #D9770620)`,
          border: `2px solid #FEF3C7`,
        }}
      >
        <div className="stat-icon" style={{ background: colors.warning }}>
          ✅
        </div>
        <div className="stat-content">
          <div className="stat-value" style={{ color: colors.textDark }}>
            {stats?.listings?.approved_listings || 0}
          </div>
          <div className="stat-label" style={{ color: colors.textLight }}>
            Approved Today
          </div>
        </div>
      </motion.div>
    </div>
  </motion.div>
);

const ListingReviewTab = ({ listings, onListingAction, colors }) => {
  const [rejectionModal, setRejectionModal] = useState({
    show: false,
    listing: null,
    reason: "",
  });

  const handleRejectClick = (listing) => {
    setRejectionModal({
      show: true,
      listing: listing,
      reason: "",
    });
  };

  const handleRejectConfirm = () => {
    if (!rejectionModal.reason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }

    onListingAction(
      rejectionModal.listing.id,
      "needs_revision",
      rejectionModal.reason
    );
    setRejectionModal({ show: false, listing: null, reason: "" });
  };

  const truncateUrl = (url, maxLength = 50) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + "...";
  };

  return (
    <motion.div
      key="listings"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-panel"
    >
      <div className="tab-header">
        <h2 style={{ color: colors.textDark }}>Listing Review</h2>
        <div className="tab-actions">
          <button
            className="action-btn"
            style={{ background: colors.primary, color: "white" }}
          >
            <AiOutlineFilter style={{ marginRight: "6px" }} />
            Filter
          </button>
        </div>
      </div>

      {listings.length === 0 ? (
        <div className="empty-state">
          <Lottie
            animationData={emptyAnim}
            style={{ width: 150, height: 150 }}
          />
          <p style={{ color: colors.textLight }}>
            No pending listings to review
          </p>
        </div>
      ) : (
        <div
          className="listings-table-container"
          style={{ overflowX: "auto", maxWidth: "100%" }}
        >
          <table
            className="admin-table"
            style={{ minWidth: "800px", tableLayout: "fixed" }}
          >
            <thead>
              <tr style={{ background: colors.background }}>
                <th style={{ color: colors.textDark, width: "150px" }}>User</th>
                <th style={{ color: colors.textDark, width: "100px" }}>Day</th>
                <th style={{ color: colors.textDark, width: "200px" }}>
                  Listing URL
                </th>
                <th style={{ color: colors.textDark, width: "120px" }}>Date</th>
                <th style={{ color: colors.textDark, width: "120px" }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {listings.map((listing) => (
                <motion.tr
                  key={listing.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="table-row"
                >
                  <td style={{ color: colors.textDark, width: "150px" }}>
                    <div style={{ fontSize: "14px", fontWeight: "500" }}>
                      {listing.first_name} {listing.last_name}
                    </div>
                    <div style={{ fontSize: "12px", color: colors.textLight }}>
                      {listing.user_type}
                    </div>
                  </td>
                  <td
                    style={{
                      color: colors.textDark,
                      textTransform: "capitalize",
                      width: "100px",
                    }}
                  >
                    {listing.week_day}
                  </td>
                  <td style={{ width: "200px" }}>
                    <a
                      href={listing.listing_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: colors.primary,
                        fontSize: "13px",
                        textDecoration: "none",
                        display: "block",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      className="listing-link"
                      title={listing.listing_url}
                    >
                      {truncateUrl(listing.listing_url, 40)}
                    </a>
                  </td>
                  <td
                    style={{
                      color: colors.textLight,
                      width: "120px",
                      fontSize: "13px",
                    }}
                  >
                    {new Date(listing.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ width: "120px" }}>
                    <div
                      className="action-buttons"
                      style={{ display: "flex", gap: "8px" }}
                    >
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="btn-success"
                        onClick={() => onListingAction(listing.id, "approved")}
                        style={{
                          background: colors.success,
                          border: "none",
                          borderRadius: "6px",
                          padding: "8px 12px",
                          cursor: "pointer",
                          color: "white",
                          fontSize: "12px",
                        }}
                        title="Approve"
                      >
                        <AiOutlineCheck />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="btn-warning"
                        onClick={() => handleRejectClick(listing)}
                        style={{
                          background: colors.warning,
                          border: "none",
                          borderRadius: "6px",
                          padding: "8px 12px",
                          cursor: "pointer",
                          color: "white",
                          fontSize: "12px",
                        }}
                        title="Request Revision"
                      >
                        <AiOutlineEdit />
                      </motion.button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Rejection Modal */}
      {rejectionModal.show && (
        <RejectionReasonModal
          listing={rejectionModal.listing}
          reason={rejectionModal.reason}
          onReasonChange={(reason) =>
            setRejectionModal((prev) => ({ ...prev, reason }))
          }
          onConfirm={handleRejectConfirm}
          onClose={() =>
            setRejectionModal({ show: false, listing: null, reason: "" })
          }
          colors={colors}
        />
      )}
    </motion.div>
  );
};

const RejectionReasonModal = ({
  listing,
  reason,
  onReasonChange,
  onConfirm,
  onClose,
  colors,
}) => {
  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <motion.div
        className="modal-content"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.card,
          borderRadius: "16px",
          padding: "24px",
          width: "90%",
          maxWidth: "500px",
          border: `2px solid ${colors.warning}`,
        }}
      >
        <div
          className="modal-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            borderBottom: `1px solid ${colors.primaryLight}`,
            paddingBottom: "16px",
          }}
        >
          <h3 style={{ color: colors.textDark, margin: 0 }}>
            Rejection Reason
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.5rem",
              cursor: "pointer",
              color: colors.textLight,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <p style={{ color: colors.textLight, marginBottom: "12px" }}>
            Please provide the reason for rejecting this listing:
          </p>
          <p
            style={{
              color: colors.textDark,
              fontWeight: "500",
              background: colors.background,
              padding: "8px 12px",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          >
            Listing:{" "}
            <a
              href={listing.listing_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary }}
            >
              {listing.listing_url}
            </a>
          </p>
        </div>

        <div className="form-group" style={{ marginBottom: "24px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              color: colors.textDark,
              fontWeight: "600",
            }}
          >
            Reason for Rejection *
          </label>
          <textarea
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            style={{
              width: "100%",
              padding: "12px",
              border: `2px solid ${colors.primaryLight}`,
              borderRadius: "8px",
              fontSize: "14px",
              background: colors.card,
              minHeight: "120px",
              resize: "none",
              lineHeight: "1.5",
            }}
            placeholder="Explain why this listing needs revision..."
            autoFocus
          />
        </div>

        <div
          style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              border: `2px solid ${colors.primaryLight}`,
              borderRadius: "8px",
              background: colors.background,
              color: colors.textDark,
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!reason.trim()}
            style={{
              padding: "10px 20px",
              border: "none",
              borderRadius: "8px",
              background: !reason.trim() ? colors.primaryLight : colors.warning,
              color: "white",
              cursor: !reason.trim() ? "not-allowed" : "pointer",
              fontWeight: "600",
              opacity: !reason.trim() ? 0.6 : 1,
            }}
          >
            Send for Revision
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};
const TaskManagementTab = ({
  tasks,
  colors,
  onRefreshTasks,
  onRefreshStats,
  onUpdateTask,
  onDeleteTask,
  users,
}) => {
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showUpdateTaskModal, setShowUpdateTaskModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [expandedTasks, setExpandedTasks] = useState(new Set());

  const handleNewTaskClick = () => {
    setShowNewTaskModal(true);
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setShowUpdateTaskModal(true);
  };

  const handleDeleteClick = (task) => {
    setSelectedTask(task);
    setShowDeleteConfirm(true);
  };

  const handleCreateTask = async (taskData) => {
    try {
      if (onRefreshTasks) await onRefreshTasks();
      if (onRefreshStats) await onRefreshStats();
      toast.success("Task created successfully!");
    } catch (error) {
      console.error("Error refreshing tasks:", error);
    }
  };

  const handleUpdateTask = async (taskData) => {
    if (selectedTask && onUpdateTask) {
      const success = await onUpdateTask(selectedTask.id, taskData);
      if (success) {
        setShowUpdateTaskModal(false);
        setSelectedTask(null);
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (selectedTask && onDeleteTask) {
      const success = await onDeleteTask(selectedTask.id);
      if (success) {
        setShowDeleteConfirm(false);
        setSelectedTask(null);
      }
    }
  };

  // Aynı title'a sahip task'ları grupla
  const groupedTasks = tasks.reduce((acc, task) => {
    const key = `${task.title}-${task.priority}-${task.category}`;
    if (!acc[key]) {
      acc[key] = {
        title: task.title,
        priority: task.priority,
        category: task.category,
        due_date: task.due_date,
        tasks: [],
      };
    }
    acc[key].tasks.push(task);
    return acc;
  }, {});

  const groupedTasksArray = Object.values(groupedTasks);

  const toggleAccordion = (taskKey) => {
    setExpandedTasks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskKey)) {
        newSet.delete(taskKey);
      } else {
        newSet.add(taskKey);
      }
      return newSet;
    });
  };

  return (
    <motion.div
      key="tasks"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-panel"
    >
      <div className="tab-header">
        <h2 style={{ color: colors.textDark }}>Task Management</h2>
        <div className="tab-actions">
          <button
            className="action-btn"
            style={{ background: colors.primary, color: "white" }}
            onClick={handleNewTaskClick}
          >
            <AiOutlinePlus style={{ marginRight: "6px" }} />
            New Task
          </button>
        </div>
      </div>

      {showNewTaskModal && (
        <NewTaskModal
          onClose={() => setShowNewTaskModal(false)}
          onCreate={handleCreateTask}
          colors={colors}
        />
      )}

      {showUpdateTaskModal && selectedTask && (
        <UpdateTaskModal
          task={selectedTask}
          onClose={() => {
            setShowUpdateTaskModal(false);
            setSelectedTask(null);
          }}
          onUpdate={handleUpdateTask}
          colors={colors}
          users={users}
        />
      )}

      {showDeleteConfirm && selectedTask && (
        <DeleteTaskConfirmation
          task={selectedTask}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setSelectedTask(null);
          }}
          colors={colors}
        />
      )}

      <div className="tasks-overview">
        <div className="tasks-stats">
          <div
            className="task-stat"
            style={{ borderLeft: `4px solid ${colors.primary}` }}
          >
            <span style={{ color: colors.textDark }}>Total Tasks</span>
            <strong style={{ color: colors.textDark }}>{tasks.length}</strong>
          </div>
          <div
            className="task-stat"
            style={{ borderLeft: `4px solid ${colors.warning}` }}
          >
            <span style={{ color: colors.textDark }}>In Progress</span>
            <strong style={{ color: colors.textDark }}>
              {tasks.filter((t) => t.status === "in_progress").length}
            </strong>
          </div>
          <div
            className="task-stat"
            style={{ borderLeft: `4px solid ${colors.success}` }}
          >
            <span style={{ color: colors.textDark }}>Completed</span>
            <strong style={{ color: colors.textDark }}>
              {tasks.filter((t) => t.status === "completed").length}
            </strong>
          </div>
          <div
            className="task-stat"
            style={{ borderLeft: `4px solid ${colors.primaryLight}` }}
          >
            <span style={{ color: colors.textDark }}>Grouped Tasks</span>
            <strong style={{ color: colors.textDark }}>
              {groupedTasksArray.length}
            </strong>
          </div>
        </div>
      </div>

      {groupedTasksArray.length === 0 ? (
        <div className="empty-state">
          <Lottie
            animationData={emptyAnim}
            style={{ width: 150, height: 150 }}
          />
          <p style={{ color: colors.textLight }}>No tasks found</p>
        </div>
      ) : (
        <div className="tasks-accordion-container">
          {groupedTasksArray.map((group, index) => {
            const taskKey = `${group.title}-${index}`;
            const isExpanded = expandedTasks.has(taskKey);
            const isGrouped = group.tasks.length > 1;

            return (
              <motion.div
                key={taskKey}
                className="task-accordion"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                style={{
                  background: colors.card,
                  border: `2px solid ${colors.primaryLight}`,
                  borderRadius: "12px",
                  marginBottom: "12px",
                  overflow: "hidden",
                }}
              >
                {/* Accordion Header */}
                <div
                  className="accordion-header"
                  onClick={() => isGrouped && toggleAccordion(taskKey)}
                  style={{
                    padding: "16px 20px",
                    background: colors.background,
                    cursor: isGrouped ? "pointer" : "default",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: isExpanded
                      ? `1px solid ${colors.primaryLight}`
                      : "none",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "8px",
                      }}
                    >
                      <h4
                        style={{
                          color: colors.textDark,
                          margin: 0,
                          fontSize: "1.1rem",
                        }}
                      >
                        {group.title}
                      </h4>
                      <span
                        className="priority-badge"
                        style={{
                          background:
                            group.priority === "urgent"
                              ? colors.error
                              : group.priority === "high"
                              ? colors.warning
                              : group.priority === "medium"
                              ? colors.primary
                              : colors.success,
                          color: "white",
                        }}
                      >
                        {group.priority}
                      </span>
                      <span
                        className="category-badge"
                        style={{
                          background: colors.primaryLight,
                          color: colors.textDark,
                          padding: "2px 8px",
                          borderRadius: "12px",
                          fontSize: "0.8rem",
                        }}
                      >
                        {group.category}
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: "16px",
                        fontSize: "0.85rem",
                        color: colors.textLight,
                      }}
                    >
                      <span>
                        📅 Due:{" "}
                        {group.due_date
                          ? new Date(group.due_date).toLocaleDateString()
                          : "No due date"}
                      </span>
                      <span>👥 Assigned to: {group.tasks.length} user(s)</span>
                      <span>
                        📊{" "}
                        {
                          group.tasks.filter((t) => t.status === "completed")
                            .length
                        }{" "}
                        completed
                      </span>
                    </div>
                  </div>

                  {isGrouped && (
                    <motion.div
                      animate={{ rotate: isExpanded ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      style={{ color: colors.primary, fontSize: "1.2rem" }}
                    >
                      ▼
                    </motion.div>
                  )}
                </div>

                {/* Accordion Content */}
                {isGrouped && isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="accordion-content"
                  >
                    <table className="admin-table" style={{ margin: 0 }}>
                      <thead>
                        <tr style={{ background: colors.background }}>
                          <th style={{ color: colors.textDark }}>
                            Assigned To
                          </th>
                          <th style={{ color: colors.textDark }}>Status</th>
                          <th style={{ color: colors.textDark }}>Progress</th>
                          <th style={{ color: colors.textDark }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.tasks.map((task) => (
                          <tr key={task.id} className="table-row">
                            <td style={{ color: colors.textDark }}>
                              {task.assigned_first_name}{" "}
                              {task.assigned_last_name}
                              <br />
                              <small style={{ color: colors.textLight }}>
                                {task.assigned_email}
                              </small>
                            </td>
                            <td>
                              <span
                                className="status-badge"
                                style={{
                                  background:
                                    task.status === "completed"
                                      ? colors.success
                                      : task.status === "in_progress"
                                      ? colors.warning
                                      : colors.primaryLight,
                                  color: colors.textDark,
                                }}
                              >
                                {task.status}
                              </span>
                            </td>
                            <td style={{ color: colors.textLight }}>
                              {new Date(task.created_at).toLocaleDateString()}
                            </td>
                            <td>
                              <div
                                className="action-buttons"
                                style={{ display: "flex", gap: "8px" }}
                              >
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleEditTask(task)}
                                  style={{
                                    background: colors.primary,
                                    border: "none",
                                    borderRadius: "6px",
                                    padding: "6px 10px",
                                    cursor: "pointer",
                                    color: "white",
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  <AiOutlineEdit />
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleDeleteClick(task)}
                                  style={{
                                    background: colors.error,
                                    border: "none",
                                    borderRadius: "6px",
                                    padding: "6px 10px",
                                    cursor: "pointer",
                                    color: "white",
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  <AiOutlineDelete />
                                </motion.button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </motion.div>
                )}

                {/* Single Task Display (not grouped) */}
                {!isGrouped && (
                  <div style={{ padding: "16px 20px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <span
                          style={{ color: colors.textDark, fontWeight: "500" }}
                        >
                          {group.tasks[0].assigned_first_name}{" "}
                          {group.tasks[0].assigned_last_name}
                        </span>
                        <br />
                        <small style={{ color: colors.textLight }}>
                          {group.tasks[0].assigned_email}
                        </small>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <span
                          className="status-badge"
                          style={{
                            background:
                              group.tasks[0].status === "completed"
                                ? colors.success
                                : group.tasks[0].status === "in_progress"
                                ? colors.warning
                                : colors.primaryLight,
                            color: colors.textDark,
                          }}
                        >
                          {group.tasks[0].status}
                        </span>
                        <div
                          className="action-buttons"
                          style={{ display: "flex", gap: "8px" }}
                        >
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleEditTask(group.tasks[0])}
                            style={{
                              background: colors.primary,
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 10px",
                              cursor: "pointer",
                              color: "white",
                              fontSize: "0.8rem",
                            }}
                          >
                            <AiOutlineEdit />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDeleteClick(group.tasks[0])}
                            style={{
                              background: colors.error,
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 10px",
                              cursor: "pointer",
                              color: "white",
                              fontSize: "0.8rem",
                            }}
                          >
                            <AiOutlineDelete />
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

// Update Task Modal Component
const UpdateTaskModal = ({ task, onClose, onUpdate, colors, users }) => {
  const [formData, setFormData] = useState({
    title: task?.title || "",
    description: task?.description || "",
    assigned_to: task?.assigned_to || "",
    priority: task?.priority || "medium",
    category: task?.category || "general",
    due_date: task?.due_date ? task.due_date.split(" ")[0] : "",
    status: task?.status || "pending",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Task title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Task description is required";
    }

    if (!formData.assigned_to) {
      newErrors.assigned_to = "Please assign to a user";
    }

    if (!formData.due_date) {
      newErrors.due_date = "Due date is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    setLoading(true);
    try {
      await onUpdate(formData);
    } catch (error) {
      console.error("Error updating task:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Bugünün tarihini YYYY-MM-DD formatında al
  const today = new Date().toISOString().split("T")[0];

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <motion.div
        className="modal-content"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.card,
          borderRadius: "16px",
          padding: "24px",
          width: "90%",
          maxWidth: "600px",
          maxHeight: "90vh",
          overflowY: "auto",
          border: `2px solid ${colors.primaryLight}`,
        }}
      >
        <div
          className="modal-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            borderBottom: `1px solid ${colors.primaryLight}`,
            paddingBottom: "16px",
          }}
        >
          <h3 style={{ color: colors.textDark, margin: 0 }}>Update Task</h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.5rem",
              cursor: "pointer",
              color: colors.textLight,
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div className="form-group" style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: colors.textDark,
                fontWeight: "600",
              }}
            >
              Task Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                border: `2px solid ${
                  errors.title ? colors.error : colors.primaryLight
                }`,
                borderRadius: "8px",
                fontSize: "14px",
                background: colors.card,
              }}
              placeholder="Enter task title"
              disabled={loading}
            />
            {errors.title && (
              <p
                style={{
                  color: colors.error,
                  fontSize: "0.8rem",
                  marginTop: "4px",
                  marginBottom: 0,
                }}
              >
                ⚠️ {errors.title}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="form-group" style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: colors.textDark,
                fontWeight: "600",
              }}
            >
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                border: `2px solid ${
                  errors.description ? colors.error : colors.primaryLight
                }`,
                borderRadius: "8px",
                fontSize: "14px",
                background: colors.card,
                minHeight: "100px",
                resize: "none",
                lineHeight: "1.5",
              }}
              placeholder="Enter task description"
              disabled={loading}
            />
            {errors.description && (
              <p
                style={{
                  color: colors.error,
                  fontSize: "0.8rem",
                  marginTop: "4px",
                  marginBottom: 0,
                }}
              >
                ⚠️ {errors.description}
              </p>
            )}
          </div>

          {/* Assigned To */}
          <div className="form-group" style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: colors.textDark,
                fontWeight: "600",
              }}
            >
              Assign To *
            </label>
            <select
              value={formData.assigned_to}
              onChange={(e) => handleChange("assigned_to", e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                border: `2px solid ${
                  errors.assigned_to ? colors.error : colors.primaryLight
                }`,
                borderRadius: "8px",
                fontSize: "14px",
                background: colors.card,
              }}
              disabled={loading}
            >
              <option value="">Select a user</option>
              {users
                .filter(
                  (user) => user.user_type === "employee" && user.status == 1
                )
                .map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.email})
                  </option>
                ))}
            </select>
            {errors.assigned_to && (
              <p
                style={{
                  color: colors.error,
                  fontSize: "0.8rem",
                  marginTop: "4px",
                  marginBottom: 0,
                }}
              >
                ⚠️ {errors.assigned_to}
              </p>
            )}
          </div>

          {/* Priority & Category */}
          <div
            className="form-row"
            style={{ display: "flex", gap: "16px", marginBottom: "20px" }}
          >
            <div className="form-group" style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: colors.textDark,
                  fontWeight: "600",
                }}
              >
                Priority *
              </label>
              <select
                value={formData.priority}
                onChange={(e) => handleChange("priority", e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${
                    errors.priority ? colors.error : colors.primaryLight
                  }`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colors.card,
                }}
                disabled={loading}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
              {errors.priority && (
                <p
                  style={{
                    color: colors.error,
                    fontSize: "0.8rem",
                    marginTop: "4px",
                    marginBottom: 0,
                  }}
                >
                  ⚠️ {errors.priority}
                </p>
              )}
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: colors.textDark,
                  fontWeight: "600",
                }}
              >
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleChange("category", e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${
                    errors.category ? colors.error : colors.primaryLight
                  }`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colors.card,
                }}
                disabled={loading}
              >
                <option value="general">General</option>
                <option value="listing_management">Listing Management</option>
                <option value="seo_optimization">SEO Optimization</option>
                <option value="ad_management">Ad Management</option>
                <option value="trademark_copyright">
                  Trademark & Copyright
                </option>
                <option value="customer_service">Customer Service</option>
                <option value="analytics_reporting">
                  Analytics & Reporting
                </option>
                <option value="inventory_management">
                  Inventory Management
                </option>
                <option value="competitor_analysis">Competitor Analysis</option>
              </select>
              {errors.category && (
                <p
                  style={{
                    color: colors.error,
                    fontSize: "0.8rem",
                    marginTop: "4px",
                    marginBottom: 0,
                  }}
                >
                  ⚠️ {errors.category}
                </p>
              )}
            </div>
          </div>

          {/* Status & Due Date */}
          <div
            className="form-row"
            style={{ display: "flex", gap: "16px", marginBottom: "20px" }}
          >
            <div className="form-group" style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: colors.textDark,
                  fontWeight: "600",
                }}
              >
                Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleChange("status", e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${colors.primaryLight}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colors.card,
                }}
                disabled={loading}
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: colors.textDark,
                  fontWeight: "600",
                }}
              >
                Due Date *
              </label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => handleChange("due_date", e.target.value)}
                min={today}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${
                    errors.due_date ? colors.error : colors.primaryLight
                  }`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colors.card,
                }}
                disabled={loading}
              />
              {errors.due_date && (
                <p
                  style={{
                    color: colors.error,
                    fontSize: "0.8rem",
                    marginTop: "4px",
                    marginBottom: 0,
                  }}
                >
                  ⚠️ {errors.due_date}
                </p>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div
            className="modal-actions"
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              marginTop: "32px",
              paddingTop: "20px",
              borderTop: `1px solid ${colors.primaryLight}`,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: "12px 24px",
                border: `2px solid ${colors.primaryLight}`,
                borderRadius: "8px",
                background: colors.background,
                color: colors.textDark,
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: "600",
                opacity: loading ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "12px 24px",
                border: "none",
                borderRadius: "8px",
                background: loading ? colors.primaryLight : colors.primary,
                color: "white",
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: "600",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? (
                <span
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid transparent",
                      borderTop: "2px solid white",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  ></div>
                  Updating...
                </span>
              ) : (
                "Update Task"
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Delete Task Confirmation Component
const DeleteTaskConfirmation = ({ task, onConfirm, onCancel, colors }) => {
  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1001,
      }}
    >
      <motion.div
        className="modal-content"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.card,
          borderRadius: "16px",
          padding: "24px",
          width: "90%",
          maxWidth: "400px",
          border: `2px solid ${colors.error}`,
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <div
            style={{
              width: "60px",
              height: "60px",
              background: colors.error,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            ⚠️
          </div>
          <h3 style={{ color: colors.textDark, marginBottom: "8px" }}>
            Delete Task
          </h3>
          <p style={{ color: colors.textLight, lineHeight: "1.5" }}>
            Are you sure you want to delete the task "
            <strong>{task?.title}</strong>"? This action cannot be undone.
          </p>
        </div>

        <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
          <button
            onClick={onCancel}
            style={{
              padding: "10px 20px",
              border: `2px solid ${colors.primaryLight}`,
              borderRadius: "8px",
              background: colors.background,
              color: colors.textDark,
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: "10px 20px",
              border: "none",
              borderRadius: "8px",
              background: colors.error,
              color: "white",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            Delete
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const UserManagementTab = ({ users, colors, onRefreshUsers }) => {
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Kullanıcıları filtrele
  useEffect(() => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // User type filter
    if (userTypeFilter !== "all") {
      filtered = filtered.filter((user) => user.user_type === userTypeFilter);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, userTypeFilter]);

  // User type değiştirme
  const handleUserTypeChange = async (userId, newType) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${settings.apiURL}/update_user_type.php`,
        {
          user_id: userId,
          user_type: newType,
        },
        { headers: authHeader() }
      );

      if (response.data.success) {
        toast.success(`User type updated to ${newType}`);
        if (onRefreshUsers) await onRefreshUsers();
      } else {
        toast.error(response.data.message || "Failed to update user type");
      }
    } catch (error) {
      console.error("Error updating user type:", error);
      toast.error("Failed to update user type");
    } finally {
      setLoading(false);
    }
  };

  // Toplu user type değiştirme
  const handleBulkTypeChange = async (newType) => {
    if (selectedUsers.size === 0) {
      toast.error("Please select users first");
      return;
    }

    setLoading(true);
    try {
      const updates = Array.from(selectedUsers).map((userId) =>
        axios.post(
          `${settings.apiURL}/update_user_type.php`,
          {
            user_id: userId,
            user_type: newType,
          },
          { headers: authHeader() }
        )
      );

      await Promise.all(updates);
      toast.success(`Updated ${selectedUsers.size} users to ${newType}`);
      setSelectedUsers(new Set());
      setShowBulkActions(false);
      if (onRefreshUsers) await onRefreshUsers();
    } catch (error) {
      console.error("Error in bulk update:", error);
      toast.error("Failed to update users");
    } finally {
      setLoading(false);
    }
  };

  // Kullanıcı seçimi
  const toggleUserSelection = (userId) => {
    setSelectedUsers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Tümünü seç
  const selectAllUsers = () => {
    setSelectedUsers(new Set(filteredUsers.map((user) => user.id)));
  };

  // Seçimi temizle
  const clearSelection = () => {
    setSelectedUsers(new Set());
  };

  // İstatistikler
  const stats = {
    total: users.length,
    employees: users.filter((u) => u.user_type === "employee").length,
    external: users.filter((u) => u.user_type === "external_store").length,
    active: users.filter((u) => {
      if (!u.last_login) return false;
      const lastLogin = new Date(u.last_login);
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      return lastLogin > weekAgo;
    }).length,
  };

  return (
    <motion.div
      key="users"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-panel"
    >
      <div className="tab-header">
        <h2 style={{ color: colors.textDark }}>User Management</h2>
        <div className="tab-actions">
          <button
            className="action-btn"
            style={{ background: colors.primary, color: "white" }}
            onClick={() => setShowBulkActions(!showBulkActions)}
          >
            <AiOutlineExport style={{ marginRight: "6px" }} />
            Bulk Actions
          </button>
        </div>
      </div>

      {/* Bulk Actions Panel */}
      {showBulkActions && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          style={{
            background: colors.background,
            border: `2px solid ${colors.primaryLight}`,
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <div>
              <strong style={{ color: colors.textDark }}>
                {selectedUsers.size} user(s) selected
              </strong>
              <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
                <button
                  onClick={selectAllUsers}
                  style={{
                    padding: "6px 12px",
                    border: `1px solid ${colors.primary}`,
                    background: "transparent",
                    color: colors.primary,
                    borderRadius: "6px",
                    fontSize: "0.8rem",
                    cursor: "pointer",
                  }}
                >
                  Select All
                </button>
                <button
                  onClick={clearSelection}
                  style={{
                    padding: "6px 12px",
                    border: `1px solid ${colors.error}`,
                    background: "transparent",
                    color: colors.error,
                    borderRadius: "6px",
                    fontSize: "0.8rem",
                    cursor: "pointer",
                  }}
                >
                  Clear Selection
                </button>
              </div>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => handleBulkTypeChange("employee")}
                disabled={loading || selectedUsers.size === 0}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  background: colors.primary,
                  color: "white",
                  borderRadius: "6px",
                  cursor:
                    loading || selectedUsers.size === 0
                      ? "not-allowed"
                      : "pointer",
                  opacity: loading || selectedUsers.size === 0 ? 0.6 : 1,
                }}
              >
                Make Employee
              </button>
              <button
                onClick={() => handleBulkTypeChange("external_store")}
                disabled={loading || selectedUsers.size === 0}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  background: colors.warning,
                  color: "white",
                  borderRadius: "6px",
                  cursor:
                    loading || selectedUsers.size === 0
                      ? "not-allowed"
                      : "pointer",
                  opacity: loading || selectedUsers.size === 0 ? 0.6 : 1,
                }}
              >
                Make External Store
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats Cards */}
      <div className="stats-grid" style={{ marginBottom: "24px" }}>
        <motion.div
          className="stat-card"
          whileHover={{ scale: 1.05 }}
          style={{
            background: `linear(135deg, ${colors.primaryLight}20, ${colors.primary}20)`,
            border: `2px solid ${colors.primaryLight}`,
          }}
        >
          <div className="stat-icon" style={{ background: colors.primary }}>
            👥
          </div>
          <div className="stat-content">
            <div className="stat-value" style={{ color: colors.textDark }}>
              {stats.total}
            </div>
            <div className="stat-label" style={{ color: colors.textLight }}>
              Total Users
            </div>
          </div>
        </motion.div>

        <motion.div
          className="stat-card"
          whileHover={{ scale: 1.05 }}
          style={{
            background: `linear(135deg, #BBF7D020, ${colors.success}20)`,
            border: `2px solid #BBF7D0`,
          }}
        >
          <div className="stat-icon" style={{ background: colors.success }}>
            💼
          </div>
          <div className="stat-content">
            <div className="stat-value" style={{ color: colors.textDark }}>
              {stats.employees}
            </div>
            <div className="stat-label" style={{ color: colors.textLight }}>
              Employees
            </div>
          </div>
        </motion.div>

        <motion.div
          className="stat-card"
          whileHover={{ scale: 1.05 }}
          style={{
            background: `linear(135deg, #FEF3C720, #D9770620)`,
            border: `2px solid #FEF3C7`,
          }}
        >
          <div className="stat-icon" style={{ background: colors.warning }}>
            🏪
          </div>
          <div className="stat-content">
            <div className="stat-value" style={{ color: colors.textDark }}>
              {stats.external}
            </div>
            <div className="stat-label" style={{ color: colors.textLight }}>
              External Stores
            </div>
          </div>
        </motion.div>

        <motion.div
          className="stat-card"
          whileHover={{ scale: 1.05 }}
          style={{
            background: `linear(135deg, #E0E7FF20, #6366F120)`,
            border: `2px solid #E0E7FF`,
          }}
        >
          <div className="stat-icon" style={{ background: "#6366F1" }}>
            🔥
          </div>
          <div className="stat-content">
            <div className="stat-value" style={{ color: colors.textDark }}>
              {stats.active}
            </div>
            <div className="stat-label" style={{ color: colors.textLight }}>
              Active (7d)
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "20px",
          padding: "16px",
          background: colors.background,
          borderRadius: "12px",
          border: `1px solid ${colors.primaryLight}`,
        }}
      >
        <div style={{ flex: 1 }}>
          <label
            style={{
              display: "block",
              marginBottom: "6px",
              color: colors.textDark,
              fontWeight: "500",
            }}
          >
            Search Users
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or email..."
            style={{
              width: "100%",
              padding: "10px 12px",
              border: `1px solid ${colors.primaryLight}`,
              borderRadius: "8px",
              fontSize: "14px",
              background: colors.card,
            }}
          />
        </div>

        <div style={{ width: "200px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "6px",
              color: colors.textDark,
              fontWeight: "500",
            }}
          >
            Filter by Type
          </label>
          <select
            value={userTypeFilter}
            onChange={(e) => setUserTypeFilter(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px",
              border: `1px solid ${colors.primaryLight}`,
              borderRadius: "8px",
              fontSize: "14px",
              background: colors.card,
            }}
          >
            <option value="all">All Users</option>
            <option value="employee">Employees</option>
            <option value="external_store">External Stores</option>
          </select>
        </div>
      </div>

      {/* Users Grid */}
      <div className="users-grid">
        {filteredUsers.map((user) => (
          <motion.div
            key={user.id}
            className="user-card"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.02, y: -5 }}
            style={{
              background: colors.card,
              border: `2px solid ${
                selectedUsers.has(user.id)
                  ? colors.primary
                  : colors.primaryLight
              }`,
              boxShadow: `0 4px 12px ${colors.primaryLight}20`,
              position: "relative",
            }}
          >
            {/* Selection Checkbox */}
            <div style={{ position: "absolute", top: "12px", right: "12px" }}>
              <input
                type="checkbox"
                checked={selectedUsers.has(user.id)}
                onChange={() => toggleUserSelection(user.id)}
                style={{
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                }}
              />
            </div>

            <div className="user-avatar" style={{ background: colors.primary }}>
              {user.first_name[0]}
              {user.last_name[0]}
            </div>

            <div className="user-info">
              <h4 style={{ color: colors.textDark, margin: "0 0 4px 0" }}>
                {user.first_name} {user.last_name}
              </h4>
              <p
                style={{
                  color: colors.textLight,
                  margin: "0 0 8px 0",
                  fontSize: "0.9rem",
                }}
              >
                {user.email}
              </p>

              <div className="user-stats">
                <span style={{ color: colors.textLight, fontSize: "0.8rem" }}>
                  📋 Tasks:{" "}
                  <strong style={{ color: colors.textDark }}>
                    {user.task_count || 0}
                  </strong>
                </span>
                <span style={{ color: colors.textLight, fontSize: "0.8rem" }}>
                  🛍️ Listings:{" "}
                  <strong style={{ color: colors.textDark }}>
                    {user.approved_listings || 0}
                  </strong>
                </span>
              </div>

              <div
                style={{
                  marginTop: "8px",
                  fontSize: "0.8rem",
                  color: colors.textLight,
                }}
              >
                Last login:{" "}
                {user.last_login
                  ? new Date(user.last_login).toLocaleDateString()
                  : "Never"}
              </div>
            </div>

            <div className="user-type">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                <span
                  className="type-badge"
                  style={{
                    background:
                      user.user_type === "employee"
                        ? colors.primary
                        : colors.warning,
                    color: "white",
                  }}
                >
                  {user.user_type === "external_store"
                    ? "External Store"
                    : user.user_type}
                </span>

                {/* User Type Toggle */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() =>
                    handleUserTypeChange(
                      user.id,
                      user.user_type === "employee"
                        ? "external_store"
                        : "employee"
                    )
                  }
                  disabled={loading}
                  style={{
                    padding: "4px 8px",
                    border: "none",
                    background: colors.primaryLight,
                    color: colors.textDark,
                    borderRadius: "4px",
                    fontSize: "0.7rem",
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  Make{" "}
                  {user.user_type === "employee"
                    ? "External Store"
                    : "Employee"}
                </motion.button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredUsers.length === 0 && (
        <div className="empty-state">
          <Lottie
            animationData={emptyAnim}
            style={{ width: 150, height: 150 }}
          />
          <p style={{ color: colors.textLight }}>
            {users.length === 0
              ? "No users found"
              : "No users match your filters"}
          </p>
        </div>
      )}
    </motion.div>
  );
};

const MeetingNotesTab = ({ meetingNotes, colors, onRefreshNotes }) => {
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleAddNote = () => {
    setEditingNote(null);
    setShowNoteModal(true);
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setShowNoteModal(true);
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const response = await axios.post(
        `${settings.apiURL}/delete_meeting_note.php`,
        { note_id: noteId },
        { headers: authHeader() }
      );

      if (response.data.success) {
        toast.success("Meeting note deleted successfully!");
        if (onRefreshNotes) await onRefreshNotes();
      } else {
        toast.error(response.data.message || "Failed to delete note");
      }
    } catch (error) {
      console.error("Error deleting note:", error);
      toast.error("Failed to delete note");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleSaveNote = async (noteData) => {
    try {
      const url = editingNote
        ? `${settings.apiURL}/update_meeting_note.php`
        : `${settings.apiURL}/add_meeting_note.php`;

      const payload = editingNote
        ? { ...noteData, note_id: editingNote.id }
        : noteData;

      const response = await axios.post(url, payload, {
        headers: authHeader(),
      });

      if (response.data.success) {
        toast.success(
          editingNote
            ? "Note updated successfully!"
            : "Note added successfully!"
        );
        setShowNoteModal(false);
        setEditingNote(null);
        if (onRefreshNotes) await onRefreshNotes();
      } else {
        toast.error(response.data.message || "Failed to save note");
      }
    } catch (error) {
      console.error("Error saving note:", error);
      toast.error("Failed to save note");
    }
  };

  return (
    <motion.div
      key="meetings"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-panel"
    >
      <div className="tab-header">
        <h2 style={{ color: colors.textDark }}>Meeting Notes</h2>
        <div className="tab-actions">
          <button
            className="action-btn"
            style={{ background: colors.primary, color: "white" }}
            onClick={handleAddNote}
          >
            <AiOutlinePlus style={{ marginRight: "6px" }} />
            Add Note
          </button>
        </div>
      </div>

      {/* Meeting Note Modal */}
      {showNoteModal && (
        <MeetingNoteModal
          note={editingNote}
          onClose={() => {
            setShowNoteModal(false);
            setEditingNote(null);
          }}
          onSave={handleSaveNote}
          colors={colors}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <DeleteConfirmationModal
          onConfirm={() => handleDeleteNote(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
          colors={colors}
        />
      )}

      <div className="meeting-notes-grid">
        {meetingNotes.map((note) => (
          <motion.div
            key={note.id}
            className="meeting-note-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.02 }}
            style={{
              background: note.is_important ? colors.background : colors.card,
              border: `2px solid ${
                note.is_important ? colors.primary : colors.primaryLight
              }`,
              borderLeft: `6px solid ${
                note.is_important ? colors.primary : colors.primaryLight
              }`,
            }}
          >
            <div className="note-header">
              <h4 style={{ color: colors.textDark, margin: 0, flex: 1 }}>
                {note.title}
              </h4>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {note.is_important && (
                  <span style={{ color: colors.primary, fontSize: "0.9rem" }}>
                    ⭐ Important
                  </span>
                )}

                {/* Action Buttons */}
                <div style={{ display: "flex", gap: "4px" }}>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleEditNote(note)}
                    style={{
                      background: colors.primaryLight,
                      border: "none",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      cursor: "pointer",
                      color: colors.textDark,
                      fontSize: "0.8rem",
                    }}
                  >
                    <AiOutlineEdit />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setDeleteConfirm(note.id)}
                    style={{
                      background: colors.error,
                      border: "none",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      cursor: "pointer",
                      color: "white",
                      fontSize: "0.8rem",
                    }}
                  >
                    <AiOutlineDelete />
                  </motion.button>
                </div>
              </div>
            </div>

            <div
              className="note-content"
              style={{
                color: colors.textLight,
                whiteSpace: "pre-wrap",
                lineHeight: "1.5",
              }}
            >
              {note.content}
            </div>

            <div className="note-footer">
              <span style={{ color: colors.textLight, fontSize: "0.8rem" }}>
                By: {note.first_name} {note.last_name}
              </span>
              <span style={{ color: colors.textLight, fontSize: "0.8rem" }}>
                {new Date(note.created_at).toLocaleDateString()}
                {note.updated_at !== note.created_at &&
                  ` (Updated: ${new Date(
                    note.updated_at
                  ).toLocaleDateString()})`}
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      {meetingNotes.length === 0 && (
        <div className="empty-state">
          <Lottie
            animationData={emptyAnim}
            style={{ width: 150, height: 150 }}
          />
          <p style={{ color: colors.textLight }}>No meeting notes yet</p>
          <button
            className="action-btn"
            style={{
              background: colors.primary,
              color: "white",
              marginTop: "16px",
            }}
            onClick={handleAddNote}
          >
            <AiOutlinePlus style={{ marginRight: "6px" }} />
            Create Your First Note
          </button>
        </div>
      )}
    </motion.div>
  );
};

const MeetingNoteModal = ({ note, onClose, onSave, colors }) => {
  const [formData, setFormData] = useState({
    title: note?.title || "",
    content: note?.content || "",
    is_important: note?.is_important || false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.content.trim()) {
      newErrors.content = "Content is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <motion.div
        className="modal-content"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.card,
          borderRadius: "16px",
          padding: "24px",
          width: "90%",
          maxWidth: "600px",
          maxHeight: "80vh",
          overflowY: "auto",
          border: `2px solid ${colors.primaryLight}`,
        }}
      >
        <div
          className="modal-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            borderBottom: `1px solid ${colors.primaryLight}`,
            paddingBottom: "16px",
          }}
        >
          <h3 style={{ color: colors.textDark, margin: 0 }}>
            {note ? "Edit Meeting Note" : "Add Meeting Note"}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.5rem",
              cursor: "pointer",
              color: colors.textLight,
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div className="form-group" style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: colors.textDark,
                fontWeight: "600",
              }}
            >
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                border: `2px solid ${
                  errors.title ? colors.error : colors.primaryLight
                }`,
                borderRadius: "8px",
                fontSize: "14px",
                background: colors.card,
              }}
              placeholder="Enter note title"
              disabled={loading}
            />
            {errors.title && (
              <p
                style={{
                  color: colors.error,
                  fontSize: "0.8rem",
                  marginTop: "4px",
                  marginBottom: 0,
                }}
              >
                ⚠️ {errors.title}
              </p>
            )}
          </div>

          {/* Content */}
          <div className="form-group" style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: colors.textDark,
                fontWeight: "600",
              }}
            >
              Content *
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => handleChange("content", e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                border: `2px solid ${
                  errors.content ? colors.error : colors.primaryLight
                }`,
                borderRadius: "8px",
                fontSize: "14px",
                background: colors.card,
                minHeight: "150px",
                resize: "none",
                lineHeight: "1.5",
              }}
              placeholder="Enter note content"
              disabled={loading}
            />
            {errors.content && (
              <p
                style={{
                  color: colors.error,
                  fontSize: "0.8rem",
                  marginTop: "4px",
                  marginBottom: 0,
                }}
              >
                ⚠️ {errors.content}
              </p>
            )}
          </div>

          {/* Important Checkbox */}
          <div className="form-group" style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: colors.textDark,
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={formData.is_important}
                onChange={(e) => handleChange("is_important", e.target.checked)}
                disabled={loading}
              />
              ⭐ Mark as Important
            </label>
          </div>

          {/* Form Actions */}
          <div
            className="modal-actions"
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              marginTop: "32px",
              paddingTop: "20px",
              borderTop: `1px solid ${colors.primaryLight}`,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: "12px 24px",
                border: `2px solid ${colors.primaryLight}`,
                borderRadius: "8px",
                background: colors.background,
                color: colors.textDark,
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: "600",
                opacity: loading ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "12px 24px",
                border: "none",
                borderRadius: "8px",
                background: loading ? colors.primaryLight : colors.primary,
                color: "white",
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: "600",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? (
                <span
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid transparent",
                      borderTop: "2px solid white",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  ></div>
                  {note ? "Updating..." : "Creating..."}
                </span>
              ) : note ? (
                "Update Note"
              ) : (
                "Create Note"
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

const DeleteConfirmationModal = ({ onConfirm, onCancel, colors }) => {
  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1001,
      }}
    >
      <motion.div
        className="modal-content"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.card,
          borderRadius: "16px",
          padding: "24px",
          width: "90%",
          maxWidth: "400px",
          border: `2px solid ${colors.error}`,
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <div
            style={{
              width: "60px",
              height: "60px",
              background: colors.error,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            ⚠️
          </div>
          <h3 style={{ color: colors.textDark, marginBottom: "8px" }}>
            Delete Meeting Note
          </h3>
          <p style={{ color: colors.textLight, lineHeight: "1.5" }}>
            Are you sure you want to delete this meeting note? This action
            cannot be undone.
          </p>
        </div>

        <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
          <button
            onClick={onCancel}
            style={{
              padding: "10px 20px",
              border: `2px solid ${colors.primaryLight}`,
              borderRadius: "8px",
              background: colors.background,
              color: colors.textDark,
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: "10px 20px",
              border: "none",
              borderRadius: "8px",
              background: colors.error,
              color: "white",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            Delete
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const NewTaskModal = ({ onClose, onCreate, colors }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    assignment_type: "specific", // Varsayılan olarak specific
    selected_users: [], // Çoklu kullanıcı seçimi
    priority: "medium",
    category: "general",
    due_date: "",
    is_recurring: false,
    recurrence_pattern: "none",
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(
        `${settings.apiURL}/get_all_users_admin.php`,
        {
          headers: authHeader(),
        }
      );
      if (response.data.success) {
        const employees = response.data.users.filter(
          (user) => user.user_type === "employee" && user.status == 1
        );
        setUsers(employees);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Task title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Task description is required";
    }

    if (
      formData.assignment_type === "specific" &&
      formData.selected_users.length === 0
    ) {
      newErrors.selected_users = "Please select at least one user";
    }

    if (!formData.priority) {
      newErrors.priority = "Priority is required";
    }

    if (!formData.category) {
      newErrors.category = "Category is required";
    }

    if (!formData.due_date) {
      newErrors.due_date = "Due date is required";
    } else {
      const selectedDate = new Date(formData.due_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.due_date = "Due date cannot be in the past";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    setLoading(true);

    try {
      const user_ids =
        formData.assignment_type === "all" ? "all" : formData.selected_users;

      const payload = {
        title: formData.title,
        description: formData.description,
        user_ids: user_ids,
        priority: formData.priority,
        category: formData.category,
        due_date: formData.due_date,
        is_recurring: formData.is_recurring,
        recurrence_pattern: formData.recurrence_pattern,
      };

      const response = await axios.post(
        `${settings.apiURL}/create_task.php`,
        payload,
        { headers: authHeader() }
      );

      if (response.data.success) {
        const taskCount = response.data.created_tasks?.length || 1;
        const assignmentText =
          formData.assignment_type === "all"
            ? "all employees"
            : `${formData.selected_users.length} user(s)`;

        toast.success(
          `Successfully created ${taskCount} task(s) for ${assignmentText}!`
        );
        onCreate(formData);
        onClose();
      } else {
        toast.error(response.data.message || "Failed to create task");
      }
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error("Failed to create task");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const toggleUserSelection = (userId) => {
    setFormData((prev) => {
      const newSelectedUsers = prev.selected_users.includes(userId)
        ? prev.selected_users.filter((id) => id !== userId)
        : [...prev.selected_users, userId];

      return {
        ...prev,
        selected_users: newSelectedUsers,
      };
    });

    // Clear error when users are selected
    if (errors.selected_users) {
      setErrors((prev) => ({
        ...prev,
        selected_users: "",
      }));
    }
  };

  const selectAllUsers = () => {
    const allUserIds = users.map((user) => user.id);
    setFormData((prev) => ({
      ...prev,
      selected_users: allUserIds,
    }));

    if (errors.selected_users) {
      setErrors((prev) => ({
        ...prev,
        selected_users: "",
      }));
    }
  };

  const clearUserSelection = () => {
    setFormData((prev) => ({
      ...prev,
      selected_users: [],
    }));
  };

  // Bugünün tarihini YYYY-MM-DD formatında al
  const today = new Date().toISOString().split("T")[0];

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <motion.div
        className="modal-content"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.card,
          borderRadius: "16px",
          padding: "24px",
          width: "90%",
          maxWidth: "600px",
          maxHeight: "90vh",
          overflowY: "auto",
          border: `2px solid ${colors.primaryLight}`,
        }}
      >
        <div
          className="modal-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            borderBottom: `1px solid ${colors.primaryLight}`,
            paddingBottom: "16px",
          }}
        >
          <h3 style={{ color: colors.textDark, margin: 0 }}>Create New Task</h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.5rem",
              cursor: "pointer",
              color: colors.textLight,
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div className="form-group" style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: colors.textDark,
                fontWeight: "600",
              }}
            >
              Task Title *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                border: `2px solid ${
                  errors.title ? colors.error : colors.primaryLight
                }`,
                borderRadius: "8px",
                fontSize: "14px",
                background: colors.card,
              }}
              placeholder="Enter task title"
              disabled={loading}
            />
            {errors.title && (
              <p
                style={{
                  color: colors.error,
                  fontSize: "0.8rem",
                  marginTop: "4px",
                  marginBottom: 0,
                }}
              >
                ⚠️ {errors.title}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="form-group" style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: colors.textDark,
                fontWeight: "600",
              }}
            >
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                border: `2px solid ${
                  errors.description ? colors.error : colors.primaryLight
                }`,
                borderRadius: "8px",
                fontSize: "14px",
                background: colors.card,
                minHeight: "100px",
                resize: "none",
                lineHeight: "1.5",
              }}
              placeholder="Enter task description"
              disabled={loading}
            />
            {errors.description && (
              <p
                style={{
                  color: colors.error,
                  fontSize: "0.8rem",
                  marginTop: "4px",
                  marginBottom: 0,
                }}
              >
                ⚠️ {errors.description}
              </p>
            )}
          </div>

          {/* Assignment Type */}
          <div className="form-group" style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "12px",
                color: colors.textDark,
                fontWeight: "600",
              }}
            >
              Assign To *
            </label>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                marginBottom: "12px",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  name="assignment_type"
                  value="all"
                  checked={formData.assignment_type === "all"}
                  onChange={(e) =>
                    handleChange("assignment_type", e.target.value)
                  }
                  disabled={loading}
                />
                <span style={{ color: colors.textDark, fontWeight: "500" }}>
                  📋 All Employees ({users.length} employees)
                </span>
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  name="assignment_type"
                  value="specific"
                  checked={formData.assignment_type === "specific"}
                  onChange={(e) =>
                    handleChange("assignment_type", e.target.value)
                  }
                  disabled={loading}
                />
                <span style={{ color: colors.textDark, fontWeight: "500" }}>
                  👤 Specific Users
                </span>
              </label>
            </div>

            {/* Çoklu Kullanıcı Seçimi */}
            {formData.assignment_type === "specific" && (
              <div style={{ marginTop: "12px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <label style={{ color: colors.textDark, fontWeight: "600" }}>
                    Select Users *
                    <span
                      style={{
                        color: colors.textLight,
                        fontWeight: "normal",
                        marginLeft: "8px",
                      }}
                    >
                      ({formData.selected_users.length} selected)
                    </span>
                  </label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      type="button"
                      onClick={selectAllUsers}
                      style={{
                        padding: "4px 8px",
                        border: `1px solid ${colors.primary}`,
                        background: "transparent",
                        color: colors.primary,
                        borderRadius: "4px",
                        fontSize: "0.8rem",
                        cursor: "pointer",
                      }}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={clearUserSelection}
                      style={{
                        padding: "4px 8px",
                        border: `1px solid ${colors.error}`,
                        background: "transparent",
                        color: colors.error,
                        borderRadius: "4px",
                        fontSize: "0.8rem",
                        cursor: "pointer",
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div
                  style={{
                    border: `2px solid ${
                      errors.selected_users ? colors.error : colors.primaryLight
                    }`,
                    borderRadius: "8px",
                    padding: "12px",
                    maxHeight: "200px",
                    overflowY: "auto",
                    background: colors.background,
                  }}
                >
                  {users.map((user) => (
                    <label
                      key={user.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "8px",
                        cursor: "pointer",
                        borderRadius: "4px",
                        background: formData.selected_users.includes(user.id)
                          ? colors.primaryLight
                          : "transparent",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.selected_users.includes(user.id)}
                        onChange={() => toggleUserSelection(user.id)}
                        disabled={loading}
                      />
                      <span style={{ color: colors.textDark }}>
                        {user.first_name} {user.last_name}
                        <small
                          style={{ color: colors.textLight, marginLeft: "8px" }}
                        >
                          {user.email}
                        </small>
                      </span>
                    </label>
                  ))}

                  {users.length === 0 && (
                    <p
                      style={{
                        color: colors.warning,
                        textAlign: "center",
                        padding: "20px",
                      }}
                    >
                      No employees found
                    </p>
                  )}
                </div>

                {errors.selected_users && (
                  <p
                    style={{
                      color: colors.error,
                      fontSize: "0.8rem",
                      marginTop: "4px",
                      marginBottom: 0,
                    }}
                  >
                    ⚠️ {errors.selected_users}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Priority & Category */}
          <div
            className="form-row"
            style={{ display: "flex", gap: "16px", marginBottom: "20px" }}
          >
            <div className="form-group" style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: colors.textDark,
                  fontWeight: "600",
                }}
              >
                Priority *
              </label>
              <select
                value={formData.priority}
                onChange={(e) => handleChange("priority", e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${
                    errors.priority ? colors.error : colors.primaryLight
                  }`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colors.card,
                }}
                disabled={loading}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
              {errors.priority && (
                <p
                  style={{
                    color: colors.error,
                    fontSize: "0.8rem",
                    marginTop: "4px",
                    marginBottom: 0,
                  }}
                >
                  ⚠️ {errors.priority}
                </p>
              )}
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: colors.textDark,
                  fontWeight: "600",
                }}
              >
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleChange("category", e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${
                    errors.category ? colors.error : colors.primaryLight
                  }`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colors.card,
                }}
                disabled={loading}
              >
                <option value="general">General</option>
                <option value="listing_management">Listing Management</option>
                <option value="seo_optimization">SEO Optimization</option>
                <option value="ad_management">Ad Management</option>
                <option value="trademark_copyright">
                  Trademark & Copyright
                </option>
                <option value="customer_service">Customer Service</option>
                <option value="analytics_reporting">
                  Analytics & Reporting
                </option>
                <option value="inventory_management">
                  Inventory Management
                </option>
                <option value="competitor_analysis">Competitor Analysis</option>
              </select>
              {errors.category && (
                <p
                  style={{
                    color: colors.error,
                    fontSize: "0.8rem",
                    marginTop: "4px",
                    marginBottom: 0,
                  }}
                >
                  ⚠️ {errors.category}
                </p>
              )}
            </div>
          </div>

          {/* Due Date */}
          <div className="form-group" style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: colors.textDark,
                fontWeight: "600",
              }}
            >
              Due Date *
            </label>
            <input
              type="date"
              value={formData.due_date}
              onChange={(e) => handleChange("due_date", e.target.value)}
              min={today}
              style={{
                width: "100%",
                padding: "12px",
                border: `2px solid ${
                  errors.due_date ? colors.error : colors.primaryLight
                }`,
                borderRadius: "8px",
                fontSize: "14px",
                background: colors.card,
              }}
              disabled={loading}
            />
            {errors.due_date && (
              <p
                style={{
                  color: colors.error,
                  fontSize: "0.8rem",
                  marginTop: "4px",
                  marginBottom: 0,
                }}
              >
                ⚠️ {errors.due_date}
              </p>
            )}
          </div>

          {/* Recurring Task */}
          <div className="form-group" style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: colors.textDark,
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={formData.is_recurring}
                onChange={(e) => handleChange("is_recurring", e.target.checked)}
                disabled={loading}
              />
              Recurring Task
            </label>
          </div>

          {formData.is_recurring && (
            <div className="form-group" style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: colors.textDark,
                  fontWeight: "600",
                }}
              >
                Recurrence Pattern
              </label>
              <select
                value={formData.recurrence_pattern}
                onChange={(e) =>
                  handleChange("recurrence_pattern", e.target.value)
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${colors.primaryLight}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  background: colors.card,
                }}
                disabled={loading}
              >
                <option value="none">One Time</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="weekdays">Weekdays (Mon-Fri)</option>
              </select>
            </div>
          )}

          {/* Form Actions */}
          <div
            className="modal-actions"
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              marginTop: "32px",
              paddingTop: "20px",
              borderTop: `1px solid ${colors.primaryLight}`,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: "12px 24px",
                border: `2px solid ${colors.primaryLight}`,
                borderRadius: "8px",
                background: colors.background,
                color: colors.textDark,
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: "600",
                opacity: loading ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "12px 24px",
                border: "none",
                borderRadius: "8px",
                background: loading ? colors.primaryLight : colors.primary,
                color: "white",
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: "600",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? (
                <span
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid transparent",
                      borderTop: "2px solid white",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  ></div>
                  Creating...
                </span>
              ) : (
                "Create Task"
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default AdminTaskManagementPage;
